'use client'

import cn from 'clsx'
import { useLenis } from 'lenis/react'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import s from './easter-egg.module.css'

export function BackroomsEasterEgg() {
  const router = useRouter()
  const pathname = usePathname()
  const hasTriggered = useRef(false)
  const [isTriggered, setIsTriggered] = useState(false)
  const [_showText, setShowText] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const overscrollAmount = useRef(0)
  const wheelTimeout = useRef<NodeJS.Timeout | null>(null)
  const navigationTimeout = useRef<NodeJS.Timeout | null>(null)

  const lenis = useLenis()

  // Handle the exit animation when we reach the backrooms
  useEffect(() => {
    if (pathname === '/backrooms' && isTriggered) {
      // Start exit animation after a brief delay to ensure the page is ready
      navigationTimeout.current = setTimeout(() => {
        setIsExiting(true)
        // Reset component state after exit animation completes
        setTimeout(() => {
          setIsTriggered(false)
          setShowText(false)
          setIsExiting(false)
          hasTriggered.current = false
        }, 1000)
      }, 100)
    }

    return () => {
      if (navigationTimeout.current) {
        clearTimeout(navigationTimeout.current)
      }
    }
  }, [pathname, isTriggered])

  useEffect(() => {
    if (!lenis) return

    const triggerBackrooms = () => {
      hasTriggered.current = true
      setIsTriggered(true)
      setTimeout(() => setShowText(true), 800)
      setTimeout(() => router.push('/backrooms'), 2000)
    }

    const handleOverscroll = (deltaY: number) => {
      overscrollAmount.current += deltaY
      if (wheelTimeout.current) clearTimeout(wheelTimeout.current)
      wheelTimeout.current = setTimeout(() => {
        overscrollAmount.current = 0
      }, 500)

      if (overscrollAmount.current > 300 && !hasTriggered.current) {
        triggerBackrooms()
      }
    }

    const handleWheel = (e: WheelEvent) => {
      if (pathname === '/backrooms') return

      const { scroll, limit } = lenis
      const isAtBottom = Math.abs(scroll - limit) < 1

      if (isAtBottom && e.deltaY > 0) {
        handleOverscroll(e.deltaY)
      }
    }

    window.addEventListener('wheel', handleWheel, { passive: true })

    return () => {
      window.removeEventListener('wheel', handleWheel)
      if (wheelTimeout.current) {
        clearTimeout(wheelTimeout.current)
      }
    }
  }, [lenis, router, pathname])

  // Reset the trigger when the component mounts
  useEffect(() => {
    hasTriggered.current = false
    overscrollAmount.current = 0
  }, [])

  if (!isTriggered) return null

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] bg-secondary text-primary flex items-center justify-center',
        isExiting ? s.curtainExit : s.curtainReveal
      )}
    >
      <p className={cn('h1')}>you've found the darkroom backrooms.</p>
    </div>
  )
}
